import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Download, Paperclip, MailCheck, Link2, AlertTriangle, Info, Plus, X, UserRound } from "lucide-react";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadReportPdf } from "@/lib/pdf";
import { useM365Status, connectOutlook, fetchEmailDraft, sendReportEmail, parseEmails, EMAIL_RE } from "@/lib/m365";
import { cn } from "@/lib/utils";

const Notice = ({ icon: Icon = Info, tone = "info", title, children, testid }) => (
  <div
    data-testid={testid}
    className={cn(
      "rounded-xl border p-4",
      tone === "warn" && "border-primary/40 bg-primary/5",
      tone === "error" && "status-overdue",
      tone === "info" && "bg-muted/40"
    )}
  >
    <div className="flex gap-3">
      <span className={cn("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg", tone === "warn" ? "bg-primary/15 text-[#9a2f00] dark:text-primary" : tone === "error" ? "bg-transparent" : "bg-muted")}>
        <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        {title && <div className="text-sm font-semibold">{title}</div>}
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  </div>
);

/**
 * SendReportDialog – send rapport-PDF til kunden frå teknikaren si eiga Outlook-postkasse (Microsoft 365).
 * Krev at brukaren har kopla til Outlook («Min konto»). Elles: fallback (last ned PDF / kople til).
 */
export const SendReportDialog = ({ report, open, onOpenChange, onSent }) => {
  const { loading: statusLoading, configured, connected, connection } = useM365Status(open);
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [form, setForm] = useState({ to: "", cc: "", subject: "", body: "", attach_pdf: true, copy_to_self: false });
  const [showCc, setShowCc] = useState(false);
  const [busy, setBusy] = useState(""); // send | connect | download
  const [sendError, setSendError] = useState(null);

  const reportId = report?.id;
  useEffect(() => {
    if (!open || !reportId) return;
    let alive = true;
    setSendError(null);
    setShowCc(false);
    setDraftLoading(true);
    fetchEmailDraft(reportId)
      .then((d) => {
        if (!alive) return;
        setDraft(d);
        setForm({ to: (d.to || []).join(", "), cc: "", subject: d.subject || "", body: d.body || "", attach_pdf: true, copy_to_self: false });
      })
      .catch(() => alive && toast.error("Kunne ikkje lage e-postutkast."))
      .finally(() => alive && setDraftLoading(false));
    return () => {
      alive = false;
    };
  }, [open, reportId]);

  const toList = useMemo(() => parseEmails(form.to), [form.to]);
  const ccList = useMemo(() => parseEmails(form.cc), [form.cc]);
  const invalid = useMemo(() => [...toList, ...ccList].filter((a) => !EMAIL_RE.test(a)), [toList, ccList]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSend = connected && toList.length > 0 && invalid.length === 0 && !!form.subject.trim() && !busy && !draftLoading;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSend) return;
    setBusy("send");
    setSendError(null);
    try {
      const res = await sendReportEmail(reportId, {
        to: toList,
        cc: ccList,
        subject: form.subject.trim(),
        body: form.body,
        attach_pdf: form.attach_pdf,
        copy_to_self: form.copy_to_self,
      });
      toast.success(`Rapporten er sendt til ${toList.join(", ")}.`, { description: res.sent_from ? `Frå ${res.sent_from} – kopi ligg i «Sende element».` : undefined });
      onSent?.(res.email, res);
      onOpenChange?.(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Sending feila. Prøv igjen.";
      setSendError({ status, detail: typeof detail === "string" ? detail : "Sending feila." });
      toast.error(typeof detail === "string" ? detail : "Sending feila.");
    } finally {
      setBusy("");
    }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      await connectOutlook(`/rapportar?open=${reportId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje starte Outlook-koplinga.");
      setBusy("");
    }
  };

  const download = async () => {
    setBusy("download");
    try {
      await downloadReportPdf(report);
    } catch (err) {
      toast.error("Kunne ikkje laste ned PDF.");
    } finally {
      setBusy("");
    }
  };

  const loading = statusLoading || draftLoading;
  const needsReconnect = sendError?.status === 409;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange?.(o)}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 p-0 sm:max-w-xl" data-testid="send-report-dialog">
        <DialogHeader className="md:border-b md:px-6 md:pb-4 md:pt-6">
          <DialogTitle className="font-heading text-xl font-bold">Send til kunde</DialogTitle>
          <DialogDescription>
            {report?.report_number}{draft?.customer_name ? ` · ${draft.customer_name}` : ""} – PDF-en blir lagt ved og sendt frå di eiga Outlook-adresse.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="contents" data-testid="send-report-form">
          <DialogBody className="md:min-h-0 md:flex-1 md:overflow-y-auto md:px-6 md:py-4">
            {loading ? (
              <div className="space-y-3" data-testid="send-report-loading">
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-11 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : !configured ? (
              <Notice icon={AlertTriangle} tone="warn" title="Microsoft 365 er ikkje sett opp enno" testid="send-report-not-configured">
                <p>Administrator må leggje inn app-registreringa frå Microsoft før rapportar kan sendast direkte frå portalen. Til då kan du laste ned PDF-en og sende han frå e-postprogrammet ditt.</p>
                <Button type="button" variant="outline" className="mt-2" onClick={download} disabled={!!busy} data-testid="send-report-fallback-download">
                  {busy === "download" ? <Loader2 className="animate-spin" /> : <Download />} Last ned PDF
                </Button>
              </Notice>
            ) : !connected ? (
              <Notice icon={Link2} tone="warn" title="Kople til Outlook først" testid="send-report-not-connected">
                <p>Du koplar Outlook-kontoen din til portalen éin gong. Etter det går rapportane frå di eiga e-postadresse, med kopi i «Sende element» – og kunden svarar direkte til deg.</p>
                <p className="text-xs">Du blir sendt til Microsoft for innlogging og kjem tilbake til denne rapporten.</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" onClick={connect} disabled={!!busy} data-testid="send-report-connect-button">
                    {busy === "connect" ? <Loader2 className="animate-spin" /> : <Link2 />} Kople til Outlook
                  </Button>
                  <Button type="button" variant="outline" onClick={download} disabled={!!busy} data-testid="send-report-fallback-download">
                    {busy === "download" ? <Loader2 className="animate-spin" /> : <Download />} Last ned PDF i staden
                  </Button>
                </div>
              </Notice>
            ) : (
              <>
                <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm" data-testid="send-report-from">
                  <MailCheck className="h-4 w-4 shrink-0 text-[#1F8A4C] dark:text-[#5BD08B]" />
                  <span className="text-muted-foreground">Frå</span>
                  <span className="min-w-0 truncate font-semibold">{connection?.account_email || "Outlook-kontoen din"}</span>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="send-report-to" className="text-sm font-semibold">Til <span className="text-primary">*</span></Label>
                    {!showCc && (
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowCc(true)} data-testid="send-report-show-cc"><Plus className="h-3.5 w-3.5" /> Kopi</Button>
                    )}
                  </div>
                  <div className="relative">
                    <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="send-report-to"
                      type="text"
                      inputMode="email"
                      autoComplete="off"
                      value={form.to}
                      onChange={(e) => set("to", e.target.value)}
                      placeholder="kunde@firma.no, fleire skilde med komma"
                      className={cn("h-11 pl-9", invalid.length > 0 && "border-destructive focus-visible:ring-destructive/40")}
                      aria-invalid={invalid.length > 0}
                      data-testid="send-report-to-input"
                    />
                  </div>
                  {draft && !draft.has_customer_email && toList.length === 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-[#9a2f00] dark:text-primary" data-testid="send-report-no-customer-email">
                      <AlertTriangle className="h-3.5 w-3.5" /> Kunden har ikkje registrert e-postadresse – skriv inn mottakar.
                    </p>
                  )}
                  {invalid.length > 0 && (
                    <p className="text-xs text-destructive" data-testid="send-report-invalid-email">Ugyldig e-postadresse: {invalid.slice(0, 3).join(", ")}</p>
                  )}
                </div>

                {showCc && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="send-report-cc" className="text-sm font-semibold">Kopi</Label>
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setShowCc(false); set("cc", ""); }} data-testid="send-report-hide-cc"><X className="h-3.5 w-3.5" /> Fjern</Button>
                    </div>
                    <Input id="send-report-cc" type="text" inputMode="email" autoComplete="off" value={form.cc} onChange={(e) => set("cc", e.target.value)} placeholder="kopi@firma.no" className="h-11" data-testid="send-report-cc-input" />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="send-report-subject" className="text-sm font-semibold">Emne <span className="text-primary">*</span></Label>
                  <Input id="send-report-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} maxLength={255} className="h-11" data-testid="send-report-subject-input" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="send-report-body" className="text-sm font-semibold">Melding</Label>
                  <Textarea id="send-report-body" rows={9} value={form.body} onChange={(e) => set("body", e.target.value)} maxLength={10000} className="text-sm leading-relaxed" data-testid="send-report-body-input" />
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <label className={cn("flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm", form.attach_pdf ? "bg-card" : "bg-muted/40")}>
                    <Switch checked={form.attach_pdf} onCheckedChange={(v) => set("attach_pdf", v)} data-testid="send-report-attach-switch" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 font-semibold"><Paperclip className="h-3.5 w-3.5" /> Legg ved PDF</span>
                      <span className="block truncate text-xs text-muted-foreground" data-testid="send-report-attachment-name">{draft?.attachment_name || "Rapport.pdf"}</span>
                    </span>
                  </label>
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm">
                    <Switch checked={form.copy_to_self} onCheckedChange={(v) => set("copy_to_self", v)} data-testid="send-report-copy-self-switch" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold">Kopi til meg</span>
                      <span className="block truncate text-xs text-muted-foreground">Blindkopi til {connection?.account_email || "deg sjølv"}</span>
                    </span>
                  </label>
                </div>

                {sendError && (
                  <Notice icon={AlertTriangle} tone="error" title={needsReconnect ? "Outlook-koplinga må fornyast" : "Sending feila"} testid="send-report-error">
                    <p>{sendError.detail}</p>
                    {needsReconnect && (
                      <Button type="button" size="sm" variant="outline" className="mt-1" onClick={connect} disabled={!!busy} data-testid="send-report-reconnect-button">
                        {busy === "connect" ? <Loader2 className="animate-spin" /> : <Link2 />} Kople til Outlook på nytt
                      </Button>
                    )}
                  </Notice>
                )}
              </>
            )}
          </DialogBody>

          <DialogFooter className="grid grid-cols-2 gap-2 md:flex md:border-t md:px-6 md:py-3 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy === "send"} data-testid="send-report-cancel">Avbryt</Button>
            {configured && connected && !loading && (
              <Button type="submit" disabled={!canSend} data-testid="send-report-submit">
                {busy === "send" ? <Loader2 className="animate-spin" /> : <Send />} Send e-post
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
